import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../../features/auth/services/auth.service';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../services/notification.service';
import { CODIGO_OPERADOR_INVALIDO, CODIGO_OPERADOR_PIN_TEMPORAL, CODIGO_OPERADOR_VENCIDO, codigoDeOperador } from '../operador/operador.service';

/** Lo mismo que renueva `authInterceptor`: un pedido a nuestras APIs que no es el login ni la propia renovación. */
function seRenueva(url: string): boolean {
  return (url.includes(environment.apiUrl) || url.includes(environment.authServiceUrl))
    && !url.includes('/auth/refresh') && !url.includes('/auth/login');
}

/** Status codes handled by the retry interceptor — no toast here */
const RETRY_STATUS_CODES = [0, 502, 503, 504];

/**
 * El mensaje que escribió la aplicación, o `null` si el cuerpo no es suyo.
 *
 * <p>Hay tres cuerpos de error dando vueltas y solo dos son nuestros:
 *
 * <ul>
 *   <li>Business responde `RestResponse`, que trae `success` y `message`.</li>
 *   <li>Auth responde `ApiError`, que trae `detail`.</li>
 *   <li><b>La cadena de seguridad no responde ninguno de los dos.</b> Un 403 de
 *       `ExceptionTranslationFilter` —token vencido, anónimo, rol equivocado—
 *       no pasa por ningún `@ControllerAdvice`: lo arma `BasicErrorController`
 *       y, con `server.error.include-message: always` (dev y QAS), el cuerpo
 *       viene `{"error":"Forbidden","message":"Access Denied"}`.</li>
 * </ul>
 *
 * <p>Sin esta distinción, «Access Denied» se mostraba como toast al usuario —en
 * inglés, contra CLAUDE.md §4.3— cada vez que vencía la sesión del portal en
 * QAS, que es justo el entorno donde se corre el smoke. En producción no se veía
 * porque ahí `include-message` es `never`, así que el `message` llegaba vacío:
 * el mismo defecto solo se manifiesta donde se prueba.
 *
 * <p>También devuelve `null` para las descargas: con `responseType: 'blob'` el
 * cuerpo del error llega como `Blob` y no tiene ninguno de los dos campos. Antes
 * eso daba el genérico por accidente; ahora por decisión.
 */
function mensajeDeLaApp(cuerpo: unknown): string | null {
  if (!cuerpo || typeof cuerpo !== 'object') return null;
  const c = cuerpo as { detail?: unknown; message?: unknown; success?: unknown };

  if (typeof c.detail === 'string' && c.detail.trim()) return c.detail;
  // `success` es la firma de RestResponse. El cuerpo de Spring Boot no lo trae,
  // así que su `message` no se lee.
  if ('success' in c && typeof c.message === 'string' && c.message.trim()) return c.message;
  return null;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const notification = inject(NotificationService);
  const authService = inject(AuthService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Va antes que cualquier otro filtro: tenant_maintenance viaja como 503 y
      // tenant_suspended como 403, así que los early returns de más abajo los
      // descartarían como error de conexión o de permisos sin mirar el cuerpo.
      // No son fallas de la operación sino de a qué instancia pertenece el
      // dominio, y un toast sobre una pantalla que igual no va a funcionar no
      // le sirve a nadie.
      const codigoTenant = error.error?.error as string | undefined;
      if (codigoTenant === 'tenant_not_found') {
        router.navigate(['/instancia-no-encontrada']);
        return throwError(() => error);
      }
      if (codigoTenant === 'tenant_maintenance' || codigoTenant === 'registro_no_disponible') {
        router.navigate(['/mantenimiento']);
        return throwError(() => error);
      }
      if (codigoTenant === 'tenant_suspended') {
        router.navigate(['/instancia-suspendida']);
        return throwError(() => error);
      }
      if (codigoTenant === 'tenant_mismatch') {
        // La sesión pertenece a otra instancia: pasa al cambiar de dominio en la
        // misma pestaña. Limpiar y volver al login del dominio actual — `logout()`
        // elige cuál según el rol de la sesión que se cierra.
        authService.logout();
        return throwError(() => error);
      }

      // La sesión del mecánico venció: la pantalla del PIN lo dice, y el operadorInterceptor repite el pedido al volver.
      const codigoOperador = codigoDeOperador(error);
      if (codigoOperador === CODIGO_OPERADOR_VENCIDO || codigoOperador === CODIGO_OPERADOR_INVALIDO
          || codigoOperador === CODIGO_OPERADOR_PIN_TEMPORAL) {
        return throwError(() => error);
      }

      // Connection errors are handled by retryInterceptor — skip toast
      if (RETRY_STATUS_CODES.includes(error.status)) {
        return throwError(() => error);
      }

      // Skip toast for requests marked as silent (e.g., RUT lookup that may 404)
      if (req.headers.has('X-Skip-Error-Toast')) {
        return throwError(() => error);
      }

      // Auth endpoints (/auth/login, /auth/register) manejan sus propios errores
      // No interferir con logout ni toasts duplicados. El logout va aparte y sin
      // aviso: sale fire-and-forget mientras la sesión ya se limpió, y un error
      // ahí (token vencido, servicio caído) aparecía como toast sobre el login.
      const esAuthEndpoint = req.url.includes('/auth/login')
        || req.url.includes('/auth/register')
        || req.url.includes('/auth/logout');
      if (esAuthEndpoint) {
        return throwError(() => error);
      }

      let errorMessage = 'Ha ocurrido un error inesperado';

      if (error.error instanceof ErrorEvent) {
        errorMessage = `Error: ${error.error.message}`;
      } else {
        switch (error.status) {
          case 400:
            errorMessage = mensajeDeLaApp(error.error) || 'Solicitud incorrecta. Verifica los datos enviados.';
            break;

          case 401:
            // Un 401 que se puede renovar no es una sesión terminada: las respuestas recorren los interceptores al
            // revés, así que este lo ve antes que `authInterceptor`. Cerrar acá borraba el token de renovación antes de
            // que el otro alcanzara a usarlo, y la tablet del taller volvía al login cada vez que vencía el de acceso.
            if (seRenueva(req.url) && authService.getRefreshToken()) {
              return throwError(() => error);
            }
            errorMessage = 'Tu sesión ha expirado. Inicia sesión nuevamente.';
            // Sin navegación propia: el destino lo decide `sesionVencida()` leyendo
            // el rol antes de limpiar —forzarlo acá mandaba al cliente del portal
            // al login del taller— y recuerda la pantalla para volver a ella.
            authService.sesionVencida();
            break;

          case 403:
            // Business manda el motivo en `message` (RestResponse) y Auth en `detail`
            // (ApiError). Leyendo solo `detail`, el 403 del portal con motivo —«esta
            // cuenta no está asociada a ningún cliente»— salía como el genérico.
            errorMessage = mensajeDeLaApp(error.error) || 'No tienes permisos para realizar esta acción.';
            break;

          case 404:
            errorMessage = mensajeDeLaApp(error.error) || 'Recurso no encontrado.';
            break;

          case 409:
            errorMessage = mensajeDeLaApp(error.error) || 'Conflicto con el estado actual del recurso.';
            break;

          case 422:
            errorMessage = mensajeDeLaApp(error.error) || 'Datos de validación incorrectos.';
            break;

          case 500:
            errorMessage = mensajeDeLaApp(error.error) || 'Error interno del servidor.';
            break;

          default:
            errorMessage = mensajeDeLaApp(error.error) || `Error del servidor: ${error.status}`;
        }
      }

      // No mostrar toast para 401 (ya redirige a login)
      if (error.status !== 401) {
        const requestId = error.error?.requestId || req.headers.get('X-Request-Id') || '';
        notification.error(errorMessage, undefined, requestId || undefined);
      }

      return throwError(() => error);
    })
  );
};
